/**
 * Connect RPC Clients
 * Typed clients for each backend service, using the shared transport.
 * Only services that have existing proto definitions are included.
 */ import { createClient } from '@connectrpc/connect';
import { AgentService, AgentToolCatalogService, ArtifactService, ArtifactStudioService, AuthService, AuthzService, BugReportService, CapabilityService, CaptureReviewService, CaptureSolutionReviewsService, CollabService, ConnectorService, ContractIntelService, ContractModelService, DeliveryService, DocumentService, DocumentsService, EtlService, GoNoGoService, GovConService, IntelligenceService, MorgansService, ProjectDataService, ProposalReviewService, ProposalSubmissionService, RagService, ResponseDocumentService, RfiFeedbackService, RfiQuestionsService, RfiSourceVersionsService, SharePointService, UserManagementService } from '@mono/proto-clients';
import { AttentionService } from '../../../../../gen/ts/mono/projectdata/v1/attention_connect';
import { PortfolioService } from '../../../../../gen/ts/mono/projectdata/v1/portfolio_connect';
import { ProposalFileQAService } from '../../../../../gen/ts/mono/projectdata/v1/proposal_file_qa_connect';
import { binaryConnectTransport, connectTransport } from './transport';
// Document processing (baltigo)
export const documentClient = createClient(DocumentService, connectTransport);
// Collaborative document editor (shirohige — ONLYOFFICE-backed)
export const documentsClient = createClient(DocumentsService, connectTransport);
export const documentsBinaryClient = createClient(DocumentsService, binaryConnectTransport);
// Artifact Studio (tesoro — generate/list/approve/package)
export const artifactStudioClient = createClient(ArtifactStudioService, connectTransport);
// Auth session surface (mariejois) — GetMe carries the tenant identity
// (name + nickname) for tenant-scoped display.
export const authClient = createClient(AuthService, connectTransport);
// Authorization (enies-lobby)
export const authzClient = createClient(AuthzService, connectTransport);
// RAG / semantic search (cp0)
export const ragClient = createClient(RagService, connectTransport);
// ETL / job processing (reverie)
export const etlClient = createClient(EtlService, connectTransport);
// Project data (zou)
export const rfiQuestionsClient = createClient(RfiQuestionsService, connectTransport);
export const rfiFeedbackClient = createClient(RfiFeedbackService, connectTransport);
export const rfiSourceVersionsClient = createClient(RfiSourceVersionsService, connectTransport);
// Writing workspace edge: drafts, versions, downloads (WRT-03, zou)
export const responseDocumentClient = createClient(ResponseDocumentService, connectTransport);
export const projectDataClient = createClient(ProjectDataService, connectTransport);
export const projectDataBinaryClient = createClient(ProjectDataService, binaryConnectTransport);
// Go/no-go records and AI drafts (zou)
export const goNoGoClient = createClient(GoNoGoService, connectTransport);
// Capture review cycles (SAD-487, zou)
export const captureReviewClient = createClient(CaptureReviewService, connectTransport);
// Proposal review sessions pinned to section versions (SAD-552, zou)
export const proposalReviewClient = createClient(ProposalReviewService, connectTransport);
// Opportunity scraping (morgans)
export const morgansClient = createClient(MorgansService, connectTransport);
// Connectors (ohara)
export const connectorClient = createClient(ConnectorService, connectTransport);
// GovCon (baltigo govcon)
export const govconClient = createClient(GovConService, connectTransport);
// IDP intelligence run orchestration (baltigo)
export const intelligenceClient = createClient(IntelligenceService, connectTransport);
// SharePoint (enies-lobby)
export const sharepointClient = createClient(SharePointService, connectTransport);
// In-app bug reporting. Served by mainmast (it holds the Jira credentials)
// through sabaody — the one mainmast surface the app itself talks to.
export const bugReportClient = createClient(BugReportService, connectTransport);
// User management (mariejois)
export const userManagementClient = createClient(UserManagementService, connectTransport);
// Contract intelligence (iceburg)
export const contractIntelClient = createClient(ContractIntelService, connectTransport);
// Capability analysis (zou)
export const capabilityClient = createClient(CapabilityService, connectTransport);
// Agent tool catalog (shanks — what the agent can do)
export const agentToolCatalogClient = createClient(AgentToolCatalogService, connectTransport);
// Artifact upload/download/processing (tesoro)
export const artifactClient = createClient(ArtifactService, connectTransport);
// Contract model v2 (ryugu ContractModelService → pangaea): duties, dates,
// teams, submissions and reviews. Every request wraps pangaea's message as
// `body`.
export const contractModelClient = createClient(ContractModelService, connectTransport);
// Delivery domain — 31 entities under /delivery (ryugu, proxies to pangaea)
export const deliveryClient = createClient(DeliveryService, connectTransport);
// Chat agent (shanks). Server-streaming Chat() flows through here —
// browser → sabaody → shanks → pangaea. This is the only chat path now;
// the express SSE bridge was retired with the chat_direct_to_shanks flag.
export const agentClient = createClient(AgentService, connectTransport);
// Collab service (shanks) — chat share lifecycle + history surface. The
// /api/chat/* and /api/shared/:token REST routes that lived on
// express-backend were retired in favour of this Connect surface, routed
// through sabaody on /mono.collab.v1.*.
export const collabClient = createClient(CollabService, connectTransport);
// Attention (SAD-431, zou): the viewer's shared-information items. Read by the
// Library's attention panel and the Dashboard's Waiting on You (SAD-1005).
export const attentionClient = createClient(AttentionService, connectTransport);
export const captureSolutionReviewsClient = createClient(CaptureSolutionReviewsService, connectTransport);
// Portfolio economics (SAD-555, zou): the one source of every Opportunity
// value total and Pursuit/Proposal count on Home, Landscapes and the export.
export const portfolioClient = createClient(PortfolioService, connectTransport);
export const proposalSubmissionClient = createClient(ProposalSubmissionService, connectTransport);
export const proposalFileQAClient = createClient(ProposalFileQAService, connectTransport);
